import React, { useState, useMemo } from 'react';
import {
  FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Divider, TextField,
  InputAdornment, Stack, FormHelperText,
} from '@mui/material';
import {
  WbSunnyOutlined as SemesterIcon,
  SearchOutlined as SearchIcon,
  InboxOutlined as EmptyIcon,
} from '@mui/icons-material';
import { DateTime } from 'luxon';

export default function SemesterSelect({
  semesters   = [],
  value       = '',
  onChange,
  label       = 'Semester',
  placeholder = 'Select a semester',
  disabled    = false,
  loading     = false,
  size        = 'small',
  fullWidth   = true,
  required    = false,
  error       = false,
  helperText  = null,
  name        = 'semester_id',
  showDates   = true,
}) {
  const [search, setSearch] = useState('');

  // Normalise to a guaranteed array — guards against null/undefined being passed in
  const safeSemesters = Array.isArray(semesters) ? semesters : [];

  const filtered = useMemo(() => {
    if (!search.trim()) return safeSemesters;
    const q = search.toLowerCase();
    return safeSemesters.filter((s) =>
      s.title?.toLowerCase().includes(q) ||
      String(s.year ?? '')?.includes(q)
    );
  }, [safeSemesters, search]);

  const handleClose = () => setSearch('');

  const formatDate = (d) => {
    try {
      return DateTime.fromJSDate(new Date(d)).toLocaleString();
    } catch {
      return null;
    }
  };

  const getId = (s) => s.semester_id ?? s.id;

  // If the controlled value doesn't exist in the current options yet,
  // fall back to '' so MUI never tries to render a missing MenuItem.
  const safeValue = safeSemesters.some((s) => getId(s) === value) ? value : '';

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      required={required}
      error={error}
    >
      <InputLabel>{loading ? 'Loading semesters…' : label}</InputLabel>
      <Select
        name={name}
        value={safeValue}
        label={loading ? 'Loading semesters…' : label}
        onChange={(e) => onChange?.(e.target.value, e)}
        onClose={handleClose}
        startAdornment={
          <SemesterIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
        }
        MenuProps={{
          autoFocus: false,
          PaperProps: { sx: { maxHeight: 340, borderRadius: 2 } },
        }}
        displayEmpty
      >
        {/* ── Search ── */}
        <Box
          sx={{ px: 1.5, pt: 1, pb: 0.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search semesters…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
          />
        </Box>

        <Divider sx={{ mb: 0.5 }} />

        {/* ── Placeholder ── */}
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">{placeholder}</Typography>
        </MenuItem>

        {/* ── Filtered results ── */}
        {filtered.length === 0 ? (
          <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 24, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="caption" color="text.disabled" display="block">
              No semesters match "{search}"
            </Typography>
          </Box>
        ) : (
          filtered.map((s) => (
            <MenuItem key={getId(s)} value={getId(s)}>
              <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
                <Typography variant="body2">
                  {s.title}{s.year ? ` — ${s.year}` : ''}
                </Typography>
                {showDates && s.date_start && s.date_end && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                  >
                    {formatDate(s.date_start)} → {formatDate(s.date_end)}
                  </Typography>
                )}
              </Stack>
            </MenuItem>
          ))
        )}

        {/* ── Count ── */}
        {search && filtered.length > 0 && (
          <Box sx={{ px: 2, py: 0.75, borderTop: '0.5px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">
              {filtered.length} of {safeSemesters.length} semester{safeSemesters.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Select>

      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}